/**
 * API Provider
 *
 * React context provider for Apollo Client
 */

import { createContext, useContext, useMemo, type FC, type PropsWithChildren } from 'react';
import { ApolloProvider, type ApolloClient, type NormalizedCacheObject } from '@apollo/client';

interface ApiContextValue {
    client: ApolloClient<NormalizedCacheObject> | null;
}

const ApiContext = createContext<ApiContextValue>({
    client: null,
});

interface ApiProviderProps {
    client: ApolloClient<NormalizedCacheObject>;
}

/**
 * API Provider component
 * Wraps children with Apollo Provider
 */
export const ApiProvider: FC<PropsWithChildren<ApiProviderProps>> = ({ children, client }) => {
    const contextValue = useMemo(() => ({ client }), [client]);

    return (
        <ApiContext.Provider value={contextValue}>
            <ApolloProvider client={client}>{children}</ApolloProvider>
        </ApiContext.Provider>
    );
};

/**
 * Hook to access API context
 */
export const useApiContext = (): ApiContextValue => {
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error('useApiContext must be used within ApiProvider');
    }
    return context;
};
